/**
 * RecommendationTab (PR-D7).
 *
 * Reads the advisory capacity recommendation from
 * `GET /api/workspaces/:workspaceId/capacity-recommendation` and
 * renders it. Advisory only — no "apply" or "upgrade now" affordance.
 *
 * States rendered:
 *   - Loading  — skeleton shimmer while first fetch is in flight.
 *   - Empty    — endpoint 404 (mode=off or non-member); tab shows a
 *                lightweight "no recommendation available" message.
 *   - Error    — 5xx or network failure; tab shows "temporarily
 *                unavailable" with one retry button.
 *   - No-action confirmation — D7-DEC-05 = B: "your plan looks right"
 *                confirmation card.
 *   - Plan-upgrade / plan-downgrade — surfaces recommended plan +
 *                exceeded dimensions + explanation.
 *   - Bundle recommendations — credit-bundle / licensed-data-bundle /
 *                storage-add-on / messaging-bundle — surface bundle
 *                SKU + explanation.
 *   - Stale banner — when the persisted recommendation is older than
 *                the 7-day threshold (D7-DEC-04 = B), a "Recalculating…"
 *                banner is shown while a background trigger fires on
 *                the next workspace state change.
 */

import { useState, useEffect, useCallback } from 'react';
import { Banner as PxBanner, List as PxList } from '@roi-pros/px-react';
import { authFetch } from '../../../utils/authFetch';

type RecommendationKind =
  | 'plan-upgrade'
  | 'plan-downgrade'
  | 'credit-bundle'
  | 'licensed-data-bundle'
  | 'storage-add-on'
  | 'messaging-bundle'
  | 'no-action';

interface PlanSnapshotEmbed {
  code: string;
  name: string;
  capacity: Record<string, number>;
}

interface BundleSnapshot {
  kind: string;
  sku: string;
  size: number;
  unit: string;
}

interface ExceededDimension {
  dimension: string;
  required: number;
  currentCap: number;
  recommendedCap: number;
  overrunBy: number;
}

interface ExplanationEntry {
  kind: string;
  dimension?: string;
  subjectId?: string;
  message: string;
}

interface RecommendationExplanation {
  recommendationKind: RecommendationKind;
  currentPlan: PlanSnapshotEmbed | null;
  recommendedPlan: PlanSnapshotEmbed | null;
  recommendedBundle: BundleSnapshot | null;
  requiredCapacity: Record<string, number>;
  currentCapacity: Record<string, number>;
  exceededDimensions: ExceededDimension[];
  contributingApps: unknown[];
  contributingOutcomes: unknown[];
  explanation: ExplanationEntry[];
  generatedAt: string;
}

interface RecommendationTabProps {
  workspaceId: string;
  authToken: string;
}

/** 7-day staleness threshold per D7-DEC-04 = B. */
const STALE_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

type FetchState =
  | { status: 'loading' }
  | { status: 'empty' }
  | { status: 'error'; message: string }
  | { status: 'ok'; recommendation: RecommendationExplanation; stale: boolean };

export function RecommendationTab({ workspaceId, authToken }: RecommendationTabProps): JSX.Element {
  const [state, setState] = useState<FetchState>({ status: 'loading' });

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const res = await authFetch(
        `/api/workspaces/${workspaceId}/capacity-recommendation`,
        authToken,
      );

      if (res.status === 404) {
        setState({ status: 'empty' });
        return;
      }
      if (res.status === 409) {
        setState({ status: 'empty' });
        return;
      }
      if (!res.ok) {
        setState({
          status: 'error',
          message: 'Recommendation temporarily unavailable',
        });
        return;
      }

      const body = (await res.json()) as {
        ok: boolean;
        recommendation?: RecommendationExplanation;
      };

      if (!body.ok || !body.recommendation) {
        setState({
          status: 'error',
          message: 'Recommendation temporarily unavailable',
        });
        return;
      }

      const generatedAtMs = new Date(body.recommendation.generatedAt).getTime();
      const stale = Number.isFinite(generatedAtMs)
        ? Date.now() - generatedAtMs > STALE_THRESHOLD_MS
        : false;

      setState({ status: 'ok', recommendation: body.recommendation, stale });
    } catch (_err) {
      setState({
        status: 'error',
        message: 'Recommendation temporarily unavailable',
      });
    }
  }, [workspaceId, authToken]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.status === 'loading') {
    return (
      <div className="animate-pulse space-y-4" data-testid="recommendation-loading">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="h-24 bg-gray-100 rounded"></div>
        <div className="h-4 bg-gray-100 rounded w-2/3"></div>
      </div>
    );
  }

  if (state.status === 'empty') {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="recommendation-empty">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          No recommendation available
        </h2>
        <p className="text-sm text-gray-600">
          Once your workspace has an active subscription, we'll surface
          any advisory capacity guidance here.
        </p>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div
        className="bg-white rounded-lg border border-amber-200 p-6"
        data-testid="recommendation-error"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          {state.message}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          We couldn't load your recommendation right now. Please try again.
        </p>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          data-testid="recommendation-retry"
        >
          Retry
        </button>
      </div>
    );
  }

  const { recommendation, stale } = state;

  // D7-DEC-05 = B: no-action confirmation card.
  if (recommendation.recommendationKind === 'no-action') {
    return (
      <div className="space-y-4">
        {stale && <StaleBanner />}
        <div
          className="bg-green-50 rounded-lg border border-green-200 p-6"
          data-testid="recommendation-no-action"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-green-900 mb-1">
                Your plan looks right
              </h2>
              <p className="text-sm text-green-800">
                Based on your workspace's current usage and outcomes,
                {recommendation.currentPlan && (
                  <> your <strong>{recommendation.currentPlan.name}</strong> plan </>
                )}
                covers everything you need. We'll let you know if anything changes.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // All non-no-action kinds share a common layout.
  return (
    <div className="space-y-4">
      {stale && <StaleBanner />}
      <div
        className="bg-white rounded-lg border border-blue-200 p-6"
        data-testid={`recommendation-${recommendation.recommendationKind}`}
      >
        <RecommendationHeadline recommendation={recommendation} />
        <RecommendationBody recommendation={recommendation} />
        <ExplanationList entries={recommendation.explanation} />
        <p className="mt-6 text-xs text-gray-500">
          Advisory only. Applying this recommendation is a manual step.
        </p>
      </div>
    </div>
  );
}

function StaleBanner(): JSX.Element {
  // B6 Platform adoption: the stale/recalculating notice now renders via the Platform
  // Experience Banner (@roi-pros/px-react), severity "warning" (preserves the amber
  // identity). The application still owns WHEN it appears (the `stale` condition at the
  // call sites) and the message text; the Platform owns HOW it renders (layout, severity
  // styling via tokens, a11y). The app-owned wrapper preserves the existing
  // `data-testid` (an application test hook); no call sites changed.
  return (
    <div data-testid="recommendation-stale-banner">
      <PxBanner api={{ severity: 'warning', message: 'Recalculating your recommendation…' }} />
    </div>
  );
}

function RecommendationHeadline({
  recommendation,
}: {
  recommendation: RecommendationExplanation;
}): JSX.Element {
  const kind = recommendation.recommendationKind;
  let headline = 'Recommendation';
  if (kind === 'plan-upgrade' && recommendation.recommendedPlan) {
    headline = `Consider upgrading to ${recommendation.recommendedPlan.name}`;
  } else if (kind === 'plan-downgrade' && recommendation.recommendedPlan) {
    headline = `You may fit ${recommendation.recommendedPlan.name}`;
  } else if (kind === 'credit-bundle') {
    headline = 'Consider adding a credit bundle';
  } else if (kind === 'licensed-data-bundle') {
    headline = 'Consider a licensed-data bundle';
  } else if (kind === 'storage-add-on') {
    headline = 'Consider a storage add-on';
  } else if (kind === 'messaging-bundle') {
    headline = 'Consider a messaging bundle';
  }
  return <h2 className="text-lg font-semibold text-gray-900 mb-2">{headline}</h2>;
}

function RecommendationBody({
  recommendation,
}: {
  recommendation: RecommendationExplanation;
}): JSX.Element {
  return (
    <dl className="mt-3 space-y-2 text-sm text-gray-700">
      {recommendation.currentPlan && (
        <div className="flex justify-between">
          <dt className="text-gray-500">Current plan</dt>
          <dd className="font-medium">{recommendation.currentPlan.name}</dd>
        </div>
      )}
      {recommendation.recommendedPlan && (
        <div className="flex justify-between">
          <dt className="text-gray-500">Recommended plan</dt>
          <dd className="font-medium">{recommendation.recommendedPlan.name}</dd>
        </div>
      )}
      {recommendation.recommendedBundle && (
        <div className="flex justify-between">
          <dt className="text-gray-500">Recommended bundle</dt>
          <dd className="font-medium">
            {recommendation.recommendedBundle.size.toLocaleString()}{' '}
            {recommendation.recommendedBundle.unit}
          </dd>
        </div>
      )}
    </dl>
  );
}

function ExplanationList({ entries }: { entries: ExplanationEntry[] }): JSX.Element {
  // B9 Platform adoption: the read-only explanation list now renders via the Platform
  // Experience List (@roi-pros/px-react). The application still owns WHEN it renders
  // (this empty-entries guard) and the entry data (each `message` becomes a list item
  // label); the Platform owns HOW the list renders (surface, item separators, list a11y
  // semantics). The app-owned wrapper preserves the existing `data-testid` (an application
  // test hook). No call sites changed. See the B9 report for the transitional visual note.
  if (entries.length === 0) return <></>;
  return (
    <div className="mt-4" data-testid="recommendation-explanation">
      <PxList
        api={{
          items: entries.map((entry, idx) => ({
            id: `${entry.kind}-${idx}`,
            label: entry.message,
          })),
        }}
      />
    </div>
  );
}
